<template>
  <div class="chart-card">
    <h5>{{ countLabel }} Requested per Year</h5>
    <div class="chart-container">
      <canvas ref="chartEl"></canvas>
    </div>
  </div>
</template>

<script setup>
import {ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import Chart from 'chart.js/auto';
import { compareDrivetrain } from '@/constants/fieldOrders'


// styling
// const styles = getComputedStyle(document.documentElement)
// const font = styles.getPropertyValue('--font-family').trim() || 'Arial, sans-serif'

const props = defineProps({
  vouchers: { type: Array, default: () => [] },
  program: { type: String, default: "core" },
})

const countLabel = computed(() =>
  props.program === "sore" ? "Equipment Units" : "Vouchers"
)

const chartEl = ref(null)
let chart = null

const chartData = computed(() => {
  const rows = props.vouchers ?? []

  const years = [...new Set(
    rows.map(v => v.voucherRequestYear).filter(Boolean)
  )].sort()

  const drivetrains = [...new Set(
    rows.map(v => v.drivetrain).filter(Boolean)
  )].sort(compareDrivetrain)
  
  const counts = {}

  for (const row of rows) {
    const year = row.voucherRequestYear
    const type = row.drivetrain
    if (!year || !type) continue

    counts[type] ??= {}
    counts[type][year] = (counts[type][year] ?? 0) + 1
  }

  const styles = getComputedStyle(document.documentElement)
  const getPropertyValue = (prop) =>
    styles.getPropertyValue(prop).trim() || "rgba(0,0,0,0.1)"

  const chartColors = [
    getPropertyValue("--disc-color-01"),
    getPropertyValue("--disc-color-02"),
    getPropertyValue("--disc-color-03"),
    getPropertyValue("--disc-color-04"),
    getPropertyValue("--disc-color-05"),
    getPropertyValue("--disc-color-06"),
    getPropertyValue("--disc-color-07"),
    getPropertyValue("--disc-color-08"),
    getPropertyValue("--disc-color-09"),
    getPropertyValue("--disc-color-10"),
    getPropertyValue("--disc-color-11"),
  ]

  const datasets = drivetrains.map((type, index) => ({
    label: type,
    data: years.map(year => counts[type]?.[year] ?? 0),
    borderWidth: 0,
    backgroundColor: chartColors[index % chartColors.length],
    borderColor: "transparent",
  }))

  return {
    labels: years,
    datasets,
  }
})


function renderChart() {
  if (!chartEl.value) return // chart element not ready
  if (chart) { 
    chart.destroy() // destroy existing chart instance if it exists
    chart = null
  }

  chart = new Chart(chartEl.value, {
    type: "bar",
    data: chartData.value,
    options: {
      indexAxis: "y",
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      plugins: {
        legend: {
          display: false,
        },        
        tooltip: {
          callbacks: {
            title(items) { return items[0]?.dataset?.label ?? ""},
            label(context) {
              const year = context.label
              const count = context.raw ?? 0
              return [
                `Year of Voucher Request: ${year}`,
                `${countLabel.value}: ${count.toLocaleString()}`,
              ]
            },
          },
        },
      },

      scales: {
        x: {
          stacked: true,
          title: {
            display: true,
            text: `Total ${countLabel.value}`,
          },
          grid: { 
            color: "rgba(0,0,0,0.08)",            
          },
        },
        y: {
          stacked: true,
          grid: {
            display: false,
          },
        },
      },
    },
  })
}

onMounted(renderChart)

watch(
  chartData, 
  () => {
    renderChart()
  }, 
  { deep: true }
)

onBeforeUnmount(() => {
  if (chart) chart.destroy()
})
</script>



<style scoped>

.chart-card {
  /* height: var(--chart-max-height); */
  background: white;
  border-radius: 0;
}


canvas {
  width: 100%;
  height: var(--chart-max-height);
}

</style>